import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { useRolePerms } from '../../features/auth/useRolePerms.js';
import { DnaRecordModal } from './DnaRecordModal.js';
import { MoveWorkerModal } from './MoveWorkerModal.js';
import { EditDeploymentModal } from './EditDeploymentModal.js';
import { AbsenceModal } from './AbsenceModal.js';
import type { AbsenceType } from '@cls/shared';

interface Deployment {
  id: string;
  code: string | null;
  workerId: string;
  workerName: string;
  workerRole: string | null;
  workerPhone: string | null;
  projectId: string;
  projectName: string | null;
  projectAddress: string | null;
  projectPostcode: string | null;
  startDate: string;
  startTime: string | null;
  duration: string;
  endDate: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  supervisorPhone: string | null;
  supervisorEmail: string | null;
  meet: string | null;
  notes: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'sick' | 'holiday';
  arrivalStatus: 'pending' | 'confirmed' | 'dna' | null;
  requesterName: string | null;
  requesterId: string | null;
  waMessageSent: boolean | null;
  dispatchedBy: string | null;
  dispatchedAt: string | null;
  dnaRecordedBy?: string | null;
  dnaRecordedAt?: string | null;
  dnaNotes?: string | null;
}

interface WorkerLite {
  id: string;
  emergency: { name?: string; phone?: string } | string | null;
  core?: boolean;
  rating?: number | null;
  rtwStatus?: string | null;
  nationality?: string | null;
}

interface ProjectLite { id: string; code: string; name: string; }

interface Props { onClose: () => void; deploymentId: string; }

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 60%, 55%)`;
}

function emergencyParts(em: WorkerLite['emergency']): { name: string; phone: string | null } | null {
  if (!em) return null;
  if (typeof em === 'object') {
    const name = (em.name ?? '').trim();
    const phone = (em.phone ?? '').trim();
    if (!name && !phone) return null;
    return { name: name || '—', phone: phone || null };
  }
  const s = em.trim();
  if (!s) return null;
  const phoneM = s.match(/(?:\+44\s?|0)(?:\d\s?){9,10}\d/);
  const emPhone = phoneM ? phoneM[0].trim() : null;
  const emName = emPhone ? s.replace(emPhone, '').replace(/[,;]+$/, '').trim() : s;
  return { name: emName || '—', phone: emPhone };
}

export function DeploymentDetailModal({ onClose, deploymentId }: Props) {
  const { isAdmin } = useRolePerms();
  const qc = useQueryClient();
  const [dnaOpen, setDnaOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [absenceType, setAbsenceType] = useState<AbsenceType | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: d } = useQuery({
    queryKey: ['deployments', deploymentId],
    queryFn: () => api.get<Deployment>(`/deployments/${deploymentId}`).then((r) => r.data),
  });
  const { data: w } = useQuery<WorkerLite | undefined>({
    queryKey: ['workers', d?.workerId],
    queryFn: () => api.get<WorkerLite>(`/workers/${d!.workerId}`).then((r) => r.data).catch(() => undefined),
    enabled: !!d?.workerId,
    retry: false,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectLite[]>('/projects').then((r) => r.data),
  });

  const updStatus = useMutation({
    mutationFn: async (status: 'active' | 'completed' | 'cancelled' | 'sick' | 'holiday') => api.patch(`/deployments/${deploymentId}`, { status }),
    onSuccess: (_data, status) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['deployments', deploymentId] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Status Updated', `${d?.code ?? ''} → ${status}`);
    },
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/deployments/${deploymentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      toast.info('Deleted', 'Record removed');
      onClose();
    },
  });

  if (!d) return null;

  const dispatchedAtFmt = d.dispatchedAt
    ? new Date(d.dispatchedAt).toLocaleString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  const em = emergencyParts(w?.emergency ?? null);
  const code = d.code ?? d.id.slice(0, 8);

  const statusButtons: Array<{ key: 'active' | 'completed' | 'cancelled' | 'sick' | 'holiday'; label: string; color: string }> = [
    { key: 'active', label: 'Active', color: 'var(--live)' },
    { key: 'completed', label: 'Completed', color: 'var(--done)' },
    { key: 'cancelled', label: 'Cancelled', color: 'var(--danger)' },
  ];
  // Legacy records can still carry a sick/holiday STATUS from before absences
  // were date-ranged — keep the button visible so ops can flip them back.
  if (d.status === 'sick') statusButtons.push({ key: 'sick', label: '🤒 Sick', color: 'var(--warn)' });
  if (d.status === 'holiday') statusButtons.push({ key: 'holiday', label: '🏖 Holiday', color: 'var(--info)' });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" id="rec-detail-body" style={{ width: 760, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, color: 'var(--faint)', fontWeight: 700, marginBottom: 2 }}>{code}</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 800 }}>Deployment Record</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className={`badge b-${d.status}`}>{d.status}</span>
            {isAdmin ? <button className="btn btn-primary btn-sm" onClick={() => setEditOpen(true)} title="Edit this record">✏ Edit</button> : null}
            <button className="close-btn" onClick={onClose} style={{ fontSize: 22 }}>×</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <div className="sec-title">Worker</div>
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 11 }}>
              <div style={{ width: 38, height: 38, minWidth: 38, borderRadius: '50%', background: avatarColor(d.workerName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#0B0F1A' }}>{initials(d.workerName)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                  <span>{d.workerName}</span>
                  {w?.core ? <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(232,164,39,.18)', color: 'var(--accent)', fontWeight: 700 }}>★ CORE</span> : null}
                  {w?.rating != null && w.rating > 0 ? <span style={{ color: '#F59E0B', fontSize: 11 }}>{'★'.repeat(Math.round(w.rating))}</span> : null}
                  {w?.rtwStatus ? <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(34,197,94,.15)', color: '#22C55E', fontWeight: 700 }}>{w.rtwStatus.toUpperCase()}</span> : null}
                  {w?.nationality ? <span title={`Nationality: ${w.nationality}`} style={{ fontSize: 9, color: 'var(--muted)', padding: '1px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4 }}>{w.nationality}</span> : null}
                </div>
                {d.workerRole ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>{d.workerRole}</div> : null}
                {d.workerPhone ? (
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    <a href={`tel:${d.workerPhone}`} style={{ color: 'var(--info)', textDecoration: 'none' }}>📞 {d.workerPhone}</a>
                  </div>
                ) : null}
                <div style={{ fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border)' }}>
                  <div style={{ color: 'var(--warn)', fontFamily: 'Syne, sans-serif', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', fontSize: 9, marginBottom: 2 }}>⚠ Emergency Contact</div>
                  {em ? (
                    <>
                      <div style={{ color: 'var(--text)', fontWeight: 500 }}>{em.name}</div>
                      {em.phone ? <a href={`tel:${em.phone}`} style={{ color: 'var(--info)', textDecoration: 'none', fontSize: 11 }}>📞 {em.phone}</a> : null}
                    </>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Not set</span>
                  )}
                </div>
              </div>
            </div>

            <div className="sec-title">Project</div>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 11, marginBottom: 12 }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)', fontSize: 11, marginBottom: 2 }}>{projects.find((p) => p.id === d.projectId)?.code ?? '—'}</div>
              <div style={{ fontWeight: 500, fontSize: 12 }}>{d.projectName ?? '—'}</div>
              {d.projectAddress ? <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>{d.projectAddress}</div> : null}
              {d.projectPostcode ? (
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(d.projectPostcode)}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 11 }}>📍{d.projectPostcode} ↗</a>
              ) : null}
            </div>

            <div className="sec-title">Dispatch</div>
            <div className="dp-row"><span className="dp-key">By</span><span className="dp-val" style={{ fontSize: 11 }}>{d.dispatchedBy ?? '—'}</span></div>
            <div className="dp-row"><span className="dp-key">At</span><span className="dp-val" style={{ fontSize: 11 }}>{dispatchedAtFmt}</span></div>
            <div className="dp-row"><span className="dp-key">WhatsApp</span><span className="dp-val" style={{ fontSize: 11 }}>{d.waMessageSent ? <span style={{ color: 'var(--wa)' }}>✓ Sent</span> : 'Not sent'}</span></div>
          </div>

          <div>
            <div className="sec-title">Deployment Details</div>
            <div className="dp-row"><span className="dp-key">Start</span><span className="dp-val" style={{ fontSize: 11, fontWeight: 500 }}>{d.startDate.slice(0, 10)} at {d.startTime ?? '—'}</span></div>
            <div className="dp-row"><span className="dp-key">Duration</span><span className="dp-val" style={{ fontSize: 11 }}>{d.duration}</span></div>
            <div className="dp-row"><span className="dp-key">End</span><span className="dp-val" style={{ fontSize: 11, fontWeight: 500 }}>{d.endDate ? d.endDate.slice(0, 10) : '—'}</span></div>
            <div className="dp-row">
              <span className="dp-key">Supervisor</span>
              <span className="dp-val" style={{ fontSize: 11 }}>
                {d.supervisorName ? (
                  <>
                    <span style={{ fontWeight: 500 }}>{d.supervisorName}</span>
                    {d.supervisorPhone ? <> · <a href={`tel:${d.supervisorPhone}`} style={{ color: 'var(--info)', textDecoration: 'none' }}>📞 {d.supervisorPhone}</a></> : null}
                  </>
                ) : '—'}
              </span>
            </div>
            <div className="dp-row"><span className="dp-key">Meet</span><span className="dp-val" style={{ fontSize: 11 }}>{d.meet ?? '—'}</span></div>

            {d.notes ? (
              <>
                <div className="divider" style={{ margin: '12px 0', height: 1, background: 'var(--border)' }}></div>
                <div className="sec-title">Notes</div>
                <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--muted)', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 9 }}>{d.notes}</div>
              </>
            ) : null}

            {d.arrivalStatus === 'dna' ? (
              <>
                <div className="divider" style={{ margin: '12px 0', height: 1, background: 'var(--border)' }}></div>
                <div className="sec-title" style={{ color: '#ef4444' }}>⚠ DNA — Did Not Arrive</div>
                <div style={{ background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                  <div className="dp-row"><span className="dp-key">Recorded By</span><span className="dp-val" style={{ fontSize: 11, fontWeight: 600 }}>{d.dnaRecordedBy ?? '—'}</span></div>
                  <div className="dp-row"><span className="dp-key">Recorded At</span><span className="dp-val" style={{ fontSize: 11 }}>{d.dnaRecordedAt ? new Date(d.dnaRecordedAt).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span></div>
                  {d.dnaNotes ? <div className="dp-row"><span className="dp-key">Notes</span><span className="dp-val" style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--muted)' }}>{d.dnaNotes}</span></div> : null}
                </div>
              </>
            ) : null}

            {isAdmin ? <>
            <div className="divider" style={{ margin: '12px 0', height: 1, background: 'var(--border)' }}></div>
            <div className="sec-title">Update Status</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {statusButtons.map(({ key, label, color }) => (
                <button
                  key={key}
                  className="btn btn-ghost btn-sm"
                  onClick={() => updStatus.mutate(key)}
                  disabled={updStatus.isPending}
                  style={d.status === key ? { borderColor: color, color } : undefined}
                >
                  {label}
                </button>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={() => setAbsenceType('sick')} title="Record a sick period — keeps the deployment in place">🤒 Sick</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAbsenceType('holiday')} title="Record a holiday period — keeps the deployment in place">🏖 Holiday</button>
            </div>
            </> : null}

            {isAdmin && d.status === 'active' && (!d.arrivalStatus || d.arrivalStatus === 'pending') ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button className="btn btn-warn btn-sm" onClick={() => setDnaOpen(true)}>⚠ Mark DNA</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setMoveOpen(true)}>↔ Move</button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          {isAdmin ? <button className="btn btn-primary" onClick={() => setEditOpen(true)}>✏ Edit Record</button> : null}
          {isAdmin ? <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => { if (confirm(`Delete ${code}?`)) del.mutate(); }}>Delete</button> : null}
        </div>
      </div>
      {dnaOpen ? <DnaRecordModal onClose={() => setDnaOpen(false)} deploymentId={deploymentId} workerName={d.workerName} projectName={d.projectName ?? d.projectId} /> : null}
      {absenceType ? <AbsenceModal onClose={() => setAbsenceType(null)} workerId={d.workerId} workerName={d.workerName} initialType={absenceType} /> : null}
      {moveOpen ? <MoveWorkerModal onClose={() => setMoveOpen(false)} deploymentId={deploymentId} workerId={d.workerId} workerName={d.workerName} workerPhone={d.workerPhone ?? undefined} currentProjectName={d.projectName ?? d.projectId} /> : null}
      {editOpen ? (
        <EditDeploymentModal
          onClose={() => setEditOpen(false)}
          deploymentId={deploymentId}
          initial={{
            workerId: d.workerId,
            projectId: d.projectId,
            startDate: d.startDate,
            startTime: d.startTime,
            duration: d.duration,
            endDate: d.endDate,
            status: d.status,
            supervisorId: d.supervisorId,
            meet: d.meet,
            notes: d.notes,
            waMessageSent: d.waMessageSent ?? false,
            requesterId: d.requesterId,
          }}
        />
      ) : null}
    </div>,
    document.body,
  );
}
